import React, { useMemo, useRef, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import { type NativePrintProps, NativeReportShell } from '~/nativeLenses/NativePrintBoard';
import { nativeLensId } from '~/nativeLenses/nativeLensId';
import dayjs from '~/timeInit';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Alert,
  Box,
  Button,
  Collapse,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import PageChatButton from '~/pages/AskRichardUnified/page/PageChatButton';
import { usePageBridge } from '~/pages/AskRichardUnified/page/pageBridge';

import { useGetReportingLeLensData } from '~/hooks/apis/reporting/useGetReportingLeLensData';

import type ApiError from '~/types/ApiError';

import { lensQueriedAtLabel } from '~/utils/lensQueriedAt';

import DailyLinesView from './DailyLinesView';
import FilterSelect from './FilterSelect';
import KpiTile from './KpiTile';
import MetricSelect from './MetricSelect';
import PivotTable, { type PivotColumn, type PivotRow } from './PivotTable';
import {
  BOARD_SELECTS,
  BOARD_VIEWS,
  type BoardFilters,
  type BoardView,
  DAILY_BOARD_PAGE_ID,
  SALES_PERIODS,
  YEAR_TYPES,
  boardRequest,
  parseBoardFilters,
  ratesOf,
  resetBoardFilters,
  sequentialGrowth,
  totalsOf,
  writeBoardFilters,
} from './boardModel';
import { type Crosstab, type Grain, bucketLabel, crosstab, shareOfColumn } from './boardTables';
import { DATE_KEY, type ValueFormat, growth, isProblem, num, ratio, readCard, text } from './cards';
import { METRICS, METRIC_TABS, type MetricView, metricOption } from './metrics';
import { type PageSection, boardEditFromBridge, dailyBoardPageView } from './pageAdapter';
import { DAILY_LINES, MONTHLY_LINES, returnedLines } from './paymentLines';

const LENS_ID = nativeLensId(DAILY_BOARD_PAGE_ID);
const SOURCE = 'lux-analytics-prod.analytics.sales_with_refunds';

const SUM_COLUMNS: Array<PivotColumn> = [
  { key: 'gross_sales_aud', label: 'Gross sales', format: 'currency' },
  { key: 'refunds_aud', label: 'Refunds', format: 'currency' },
  { key: 'net_sales_aud', label: 'Net sales', format: 'currency' },
  { key: 'net_margin_aud', label: 'Gross margin (AR)', format: 'currency' },
];
const PRODUCT_COLUMNS: Array<PivotColumn> = [
  { key: 'net_sales_aud', label: 'Gross Sales (AR)', format: 'currency' },
  { key: 'cash_ar_aud', label: 'Cash Sales (AR)', format: 'currency' },
  { key: 'credit_ar_aud', label: 'Credit Sales (AR)', format: 'currency' },
];
const DAILY_VIEW_EXCLUDED = new Set(['net_sales_aud', 'net_margin_aud']);
const ATTACH = [
  { value: 'orders_with_flight', label: 'Flight' },
  { value: 'orders_with_insurance', label: 'Insurance' },
  { value: 'orders_with_experience', label: 'Experience' },
] as const;

const sydneyDate = (date: string) => dayjs(date).format('D MMM YYYY');
const sydneyRange = ({ from, to }: { from: string; to: string }) =>
  from === to ? sydneyDate(from) : `${sydneyDate(from)} to ${sydneyDate(to)}`;

const rowsOf = (
  rows: ReadonlyArray<Record<string, unknown>>,
  label: (row: Record<string, unknown>) => string,
  columns: Array<PivotColumn>,
): Array<PivotRow> =>
  rows.map((row) => ({
    label: label(row),
    values: Object.fromEntries(columns.map((column) => [column.key, num(row[column.key])])),
  }));

const totalOf = (rows: Array<PivotRow>, columns: Array<PivotColumn>): PivotRow => ({
  label: 'Total',
  values: Object.fromEntries(
    columns.map((column) => {
      const known = rows.map((row) => row.values[column.key]).filter((value): value is number => value !== null);
      return [column.key, known.length ? known.reduce((sum, value) => sum + value, 0) : null];
    }),
  ),
});

function DailyBoardContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [attach, setAttach] = useState<(typeof ATTACH)[number]['value']>('orders_with_flight');
  // On phones the 11 filters would push every KPI below the first screen, so they start collapsed.
  const isPhone = useMediaQuery(useTheme().breakpoints.down('sm'));
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filters = useMemo(() => parseBoardFilters(new URLSearchParams(location.search)), [location.search]);
  const update = (change: Partial<BoardFilters>, replace = false) =>
    navigate({
      pathname: location.pathname,
      search: writeBoardFilters({ ...filters, ...change }, new URLSearchParams(location.search)).toString(),
    }, { replace });
  const request = useMemo(() => boardRequest(filters), [filters]);
  const query = useGetReportingLeLensData(LENS_ID, request);
  const cards = query.data?.cards ?? [];
  const lastOptions = useRef<Record<string, Array<string>>>({});
  if (query.data?.filterOptions && Object.keys(query.data.filterOptions).length) {
    lastOptions.current = query.data.filterOptions;
  }
  const options = lastOptions.current;
  const ready = query.isSuccess && !query.isFetching && !query.isShowingPreviousData;
  const failure = query.error as ApiError | null;
  const card = (id: string) => readCard(cards, id);
  const problemOf = (id: string) => {
    const result = card(id);
    return isProblem(result) ? (query.isFetching ? 'Loading…' : result.problem) : null;
  };
  const rowsFor = (id: string) => {
    const result = card(id);
    return isProblem(result) ? [] : result.rows;
  };
  const truncatedFor = (id: string) => {
    const result = card(id);
    return !isProblem(result) && result.truncated;
  };

  const kpiCard = cards.find((item) => item.cardId === 'd_kpis');
  const countCard = cards.find((item) => item.cardId === 'd_kpi_counts');
  const periodLabel = kpiCard?.period
    ? `${kpiCard.period.current.from} to ${kpiCard.period.current.to}${
        kpiCard.period.comparison ? ` vs ${kpiCard.period.comparison.from} to ${kpiCard.period.comparison.to}` : ''
      }`
    : '';
  const current = totalsOf({ ...kpiCard?.rows?.[0], ...countCard?.rows?.[0] });
  const previous = totalsOf({ ...kpiCard?.comparison?.rows?.[0], ...countCard?.comparison?.rows?.[0] });
  const rates = ratesOf(current);
  const previousRates = ratesOf(previous);
  const audShare = (() => {
    const currencies = rowsFor('s_currency');
    const aud = currencies.find((row) => text(row.sales_currency) === 'AUD');
    const all = currencies.reduce<number | null>((sum, row) => {
      const amount = num(row.gross_sales_aud);
      return amount === null ? sum : (sum ?? 0) + amount;
    }, null);
    return ratio(num(aud?.gross_sales_aud), all);
  })();
  const pct = (value: number | null) => (value === null ? 'vs prior: —' : `vs prior: ${(value * 100).toFixed(1)}%`);
  const tiles: Array<{ label: string; value: number | null; format: ValueFormat; note?: string }> = [
    {
      label: 'TTV',
      value: current.grossSales,
      format: 'currency',
      note: pct(growth(current.grossSales, previous.grossSales)),
    },
    {
      label: 'AOV',
      value: rates.averageOrderValue,
      format: 'currency',
      note: pct(growth(rates.averageOrderValue, previousRates.averageOrderValue)),
    },
    {
      label: 'Unique Orders',
      value: current.orders,
      format: 'count',
      note: pct(growth(current.orders, previous.orders)),
    },
    {
      label: 'Unique Buyers',
      value: current.customers,
      format: 'count',
      note: pct(growth(current.customers, previous.customers)),
    },
    { label: 'Percent AUD Sales', value: audShare, format: 'percent' },
    {
      label: 'Lead-time',
      value: rates.leadtime,
      format: 'count',
      note: pct(growth(rates.leadtime, previousRates.leadtime)),
    },
  ];

  const dateRows = (id: string, grain: Grain, columns: Array<PivotColumn>) =>
    rowsOf(rowsFor(id), (row) => bucketLabel(row[DATE_KEY], grain), columns);
  // The payment and refund lines show once svc-reporting returns them; until then, the lines it has.
  const dailyColumns = returnedLines(DAILY_LINES, rowsFor('d_daily'));
  const dailyRows = dateRows('d_daily', 'day', dailyColumns);
  const dailyCounts = new Map(rowsFor('d_daily_orders').map((row) => [bucketLabel(row[DATE_KEY], 'day'), row]));
  const dailyWithOrders = dailyRows.map((row) => ({
    ...row,
    values: {
      ...row.values,
      orders: num(dailyCounts.get(row.label)?.orders),
      customers: num(dailyCounts.get(row.label)?.customers),
    },
  }));
  const dailyLines = dailyColumns.filter((column) => !DAILY_VIEW_EXCLUDED.has(column.key));
  const recentDays = [...dailyRows].reverse();
  const productRows: Array<PivotRow> = rowsFor('d_product')
    .map((row) => ({
      label: text(row.product),
      values: {
        net_sales_aud: num(row.net_sales_aud),
        cash_ar_aud: (num(row.cash_sales_aud) ?? 0) - (num(row.cash_refunds_aud) ?? 0),
        credit_ar_aud: (num(row.credit_sales_aud) ?? 0) - (num(row.credit_refunds_aud) ?? 0),
      },
    }))
    .sort((a, b) => (b.values.net_sales_aud ?? 0) - (a.values.net_sales_aud ?? 0));
  const queriedAt = lensQueriedAtLabel(cards);

  const monthlyCounts = new Map(rowsFor('m_monthly_counts').map((row) => [text(row[DATE_KEY]), row]));
  const monthly = rowsFor('m_monthly').map((row) => {
    const counts = monthlyCounts.get(text(row[DATE_KEY]));
    const totals = totalsOf({ ...row, ...counts });
    const monthRates = ratesOf(totals);
    return {
      key: text(row[DATE_KEY]),
      label: bucketLabel(row[DATE_KEY], 'month'),
      row,
      totals,
      rates: monthRates,
    };
  });
  const netGrowth = sequentialGrowth(monthly.map((month) => ({ key: month.key, value: month.totals.netSales })));
  const orderGrowth = sequentialGrowth(monthly.map((month) => ({ key: month.key, value: month.totals.orders })));
  const monthlyLines = returnedLines(MONTHLY_LINES, rowsFor('m_monthly'));
  const monthlyColumns: Array<PivotColumn> = [
    ...SUM_COLUMNS,
    ...monthlyLines,
    { key: 'orders', label: 'Orders', format: 'count' },
    { key: 'customers', label: 'Customers', format: 'count' },
    { key: 'aov', label: 'AOV', format: 'currency' },
    { key: 'leadtime', label: 'Lead time (days)', format: 'decimal' },
    { key: 'netGrowth', label: 'Net sales MoM', format: 'percent' },
    { key: 'orderGrowth', label: 'Orders MoM', format: 'percent' },
  ];
  const monthlyRows: Array<PivotRow> = monthly.map((month, index) => ({
    label: month.label,
    values: {
      gross_sales_aud: month.totals.grossSales,
      refunds_aud: month.totals.refunds,
      net_sales_aud: month.totals.netSales,
      net_margin_aud: month.totals.margin,
      ...Object.fromEntries(monthlyLines.map((line) => [line.key, num(month.row[line.key])])),
      orders: month.totals.orders,
      customers: month.totals.customers,
      aov: month.rates.averageOrderValue,
      leadtime: month.rates.leadtime,
      netGrowth: netGrowth[index].growth,
      orderGrowth: orderGrowth[index].growth,
    },
  }));

  /** A Metrics Filter tab's matrix in its chosen metric. */
  const metricView = (view: MetricView) => {
    const tab = METRIC_TABS[view];
    const option = metricOption(view, filters.metrics[view]);
    const metric = METRICS[option.metric];
    const table = crosstab(
      card(option.cardId),
      tab.dimension.key,
      tab.grain,
      metric.format,
      { measure: metric.measure },
      metric.additive,
    );
    return { option, metric, table };
  };
  const metricViews = {
    vertical: metricView('vertical'),
    region: metricView('region'),
    supply: metricView('supply'),
    sale: metricView('sale'),
    refunds: metricView('refunds'),
  };
  const metricControl = (view: MetricView) => (
    <MetricSelect
      view={view}
      value={metricViews[view].option.metric}
      onChange={(metric) => update({ metrics: { ...filters.metrics, [view]: metric } })}
    />
  );
  const metricLabel = (view: MetricView) => metricViews[view].metric.label;
  const metricTable = (view: MetricView, title: string, subtitle: string) =>
    crosstabTable(
      title,
      metricViews[view].table,
      METRIC_TABS[view].dimension.label,
      metricViews[view].option.cardId,
      subtitle,
    );

  const tables: Record<string, Crosstab> = {
    items: crosstab(card('i_region'), 'deal_region', 'month', 'count', { measure: 'items' }, false),
    itemsPerBasket: crosstab(
      card('i_region'),
      'deal_region',
      'month',
      'decimal',
      { top: 'items', base: 'orders' },
      false,
    ),
    attachCategory: crosstab(
      card('a_category'),
      'product_category',
      'month',
      'percent',
      { top: attach, base: 'orders' },
      false,
    ),
    attachRegion: crosstab(card('a_region'), 'deal_region', 'month', 'percent', { top: attach, base: 'orders' }, false),
  };
  const regionShare = shareOfColumn(metricViews.region.table);
  const currencyRows = rowsOf(rowsFor('s_currency'), (row) => text(row.sales_currency), SUM_COLUMNS).sort(
    (a, b) => (b.values.net_sales_aud ?? 0) - (a.values.net_sales_aud ?? 0),
  );
  const dealColumns = SUM_COLUMNS.filter((column) => column.key !== 'net_margin_aud');
  const dealRows = rowsOf(
    rowsFor('s_deals'),
    (row) => `${text(row.deal_name)} · ${text(row.supplier)}`,
    dealColumns,
  ).sort((a, b) => (b.values.net_sales_aud ?? 0) - (a.values.net_sales_aud ?? 0));

  const crosstabTable = (title: string, table: Crosstab, rowLabel: string, cardId: string, subtitle?: string) => (
    <PivotTable
      title={title}
      subtitle={subtitle}
      rowLabel={rowLabel}
      columns={table.columns}
      rows={table.rows}
      total={table.total}
      problem={problemOf(cardId)}
      truncated={truncatedFor(cardId)}
      csvName={`daily-board-${cardId}.csv`}
    />
  );

  const views: Record<BoardView, { sections: Array<PageSection>; body: React.ReactNode }> = {
    daily: {
      sections: [
        { title: 'Daily View', rows: dailyWithOrders, truncated: false },
        { title: 'Net Sales by product', rows: productRows, truncated: false },
      ],
      body: (
        <Stack spacing={2}>
          <DailyLinesView
            title="Daily View"
            subtitle="Daily sales broken down by payment type, last 3 days"
            lines={dailyLines}
            days={recentDays}
            problem={problemOf('d_daily')}
          />
          <PivotTable
            title="Net Sales"
            subtitle={`${filters.salesPeriod} net sales by product`}
            rowLabel="Product"
            columns={PRODUCT_COLUMNS}
            rows={productRows}
            total={{ ...totalOf(productRows, PRODUCT_COLUMNS), label: 'Grand Total' }}
            problem={problemOf('d_product')}
            csvName="daily-board-net-sales.csv"
            maxHeight="none"
          />
        </Stack>
      ),
    },
    monthly: {
      sections: [{ title: 'Monthly View', rows: monthlyRows, truncated: false }],
      body: (
        <PivotTable
          title="Monthly View and Month-over-Month Growth"
          subtitle="Last 13 months to yesterday; the latest month is partial."
          rowLabel="Month"
          columns={monthlyColumns}
          rows={monthlyRows}
          problem={problemOf('m_monthly') ?? problemOf('m_monthly_counts')}
          csvName="daily-board-monthly.csv"
        />
      ),
    },
    vertical: {
      sections: [
        {
          title: `${metricLabel('vertical')} by product category`,
          rows: metricViews.vertical.table.rows,
          truncated: false,
        },
      ],
      body: (
        <Stack spacing={2}>
          {metricControl('vertical')}
          {metricTable(
            'vertical',
            `${metricLabel('vertical')} by product category`,
            `${metricLabel('vertical')} by month`,
          )}
        </Stack>
      ),
    },
    region: {
      sections: [
        { title: `${metricLabel('region')} by deal region`, rows: metricViews.region.table.rows, truncated: false },
        {
          title: `Share of ${metricLabel('region').toLowerCase()} by deal region`,
          rows: regionShare.rows,
          truncated: false,
        },
      ],
      body: (
        <Stack spacing={2}>
          {metricControl('region')}
          {metricTable('region', `${metricLabel('region')} by deal region`, `${metricLabel('region')} by month`)}
          {crosstabTable(
            `Share of ${metricLabel('region').toLowerCase()} by deal region`,
            regionShare,
            'Deal region',
            metricViews.region.option.cardId,
            `Share of each month’s ${metricLabel('region').toLowerCase()}`,
          )}
        </Stack>
      ),
    },
    items: {
      sections: [{ title: 'Items sold by deal region', rows: tables.items.rows, truncated: false }],
      body: (
        <Stack spacing={2}>
          {crosstabTable(
            'Items sold by deal region',
            tables.items,
            'Deal region',
            'i_region',
            'Distinct items sold by month',
          )}
          {crosstabTable(
            'Items per basket',
            tables.itemsPerBasket,
            'Deal region',
            'i_region',
            'Distinct items ÷ distinct orders',
          )}
        </Stack>
      ),
    },
    attach: {
      sections: [
        { title: `Attach rates by product category (${attach})`, rows: tables.attachCategory.rows, truncated: false },
        { title: `Attach rates by deal region (${attach})`, rows: tables.attachRegion.rows, truncated: false },
      ],
      body: (
        <Stack spacing={2}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={attach}
            onChange={(_, value) => value && setAttach(value)}
            aria-label="Attached product"
          >
            {ATTACH.map((option) => (
              <ToggleButton key={option.value} value={option.value}>
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {crosstabTable(
            'Attach rates by product category',
            tables.attachCategory,
            'Product category',
            'a_category',
            'Orders including the product ÷ orders',
          )}
          {crosstabTable(
            'Attach rates by deal region',
            tables.attachRegion,
            'Deal region',
            'a_region',
            'Orders including the product ÷ orders',
          )}
        </Stack>
      ),
    },
    supplier: {
      sections: [
        { title: 'Net Sales by Currency', rows: currencyRows, truncated: false },
        { title: 'Sales by Deals', rows: dealRows, truncated: truncatedFor('s_deals') },
      ],
      body: (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, xl: 5 }}>
            <PivotTable
              title="Net Sales by Currency"
              subtitle={periodLabel}
              rowLabel="Sales currency"
              columns={SUM_COLUMNS}
              rows={currencyRows}
              total={totalOf(currencyRows, SUM_COLUMNS)}
              problem={problemOf('s_currency')}
              csvName="daily-board-currency.csv"
            />
          </Grid>
          <Grid size={{ xs: 12, xl: 7 }}>
            <PivotTable
              title="Sales by Deals"
              subtitle="Largest 2,000 deal and supplier pairs by net sales"
              rowLabel="Deal · Supplier"
              columns={dealColumns}
              rows={dealRows}
              problem={problemOf('s_deals')}
              csvName="daily-board-deals.csv"
            />
          </Grid>
        </Grid>
      ),
    },
    supply: {
      sections: [
        {
          title: `${metricLabel('supply')} by point of supply`,
          rows: metricViews.supply.table.rows,
          truncated: false,
        },
      ],
      body: (
        <Stack spacing={2}>
          {metricControl('supply')}
          {metricTable(
            'supply',
            `${metricLabel('supply')} by point of supply`,
            `${metricLabel('supply')} by calendar year`,
          )}
        </Stack>
      ),
    },
    sale: {
      sections: [
        { title: `${metricLabel('sale')} by point of sale`, rows: metricViews.sale.table.rows, truncated: false },
      ],
      body: (
        <Stack spacing={2}>
          {metricControl('sale')}
          {metricTable('sale', `${metricLabel('sale')} by point of sale`, `${metricLabel('sale')} by calendar year`)}
        </Stack>
      ),
    },
    refunds: {
      sections: [
        { title: `${metricLabel('refunds')} by deal region`, rows: metricViews.refunds.table.rows, truncated: false },
      ],
      body: (
        <Stack spacing={2}>
          {metricControl('refunds')}
          {metricTable(
            'refunds',
            `${metricLabel('refunds')} by deal region`,
            metricViews.refunds.option.metric === 'refundedOrders'
              ? 'Distinct orders refunded by quarter'
              : 'Accounting refunds by quarter',
          )}
        </Stack>
      ),
    },
  };

  const pageView = dailyBoardPageView({
    ready,
    filters,
    options,
    period: periodLabel,
    summary: Object.fromEntries(tiles.map((tile) => [tile.label, tile.value])),
    sections: views[filters.view].sections,
    attach,
    attachOptions: ATTACH.map((option) => option.value),
  });
  usePageBridge({
    pageId: DAILY_BOARD_PAGE_ID,
    view: pageView,
    edit: (changes) => {
      if (!ready) {
        throw new Error('Page data is still loading.');
      }
      const next = boardEditFromBridge(changes, pageView, filters, options);
      if ('attachedProduct' in changes) {
        setAttach(changes.attachedProduct as (typeof ATTACH)[number]['value']);
      }
      update(next, true);
    },
  });

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h4">Daily Sales Transaction Breakdown</Typography>
            <Typography variant="body2" color="text.secondary">
              {kpiCard?.period
                ? [`${filters.salesPeriod}: ${sydneyRange(kpiCard.period.current)}`, queriedAt]
                    .filter(Boolean)
                    .join(' · ')
                : 'Loading…'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <PageChatButton />
            <Button onClick={() => update(resetBoardFilters(filters))}>Reset filters</Button>
          </Stack>
        </Stack>
        {isPhone && (
          <Button
            variant="text"
            onClick={() => setFiltersOpen((open) => !open)}
            endIcon={filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            aria-expanded={filtersOpen}
            aria-controls="daily-board-filters"
            sx={{ alignSelf: 'flex-start' }}
          >
            {filtersOpen ? 'Hide filters' : 'Filters'}
          </Button>
        )}
        <Collapse in={!isPhone || filtersOpen} id="daily-board-filters">
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="daily-board-period">Sales period</InputLabel>
              <Select
                labelId="daily-board-period"
                label="Sales period"
                value={filters.salesPeriod}
                onChange={(event) => update({ salesPeriod: event.target.value })}
              >
                {SALES_PERIODS.map((period) => (
                  <MenuItem key={period} value={period}>
                    {period}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="daily-board-year">Calendar Year or FY</InputLabel>
              <Select
                labelId="daily-board-year"
                label="Calendar Year or FY"
                value={filters.yearType}
                onChange={(event) => update({ yearType: event.target.value })}
              >
                {YEAR_TYPES.map((yearType) => (
                  <MenuItem key={yearType} value={yearType}>
                    {yearType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FilterSelect
              label="Product category"
              options={options.product_category ?? []}
              value={filters.productCategory}
              onChange={(value) => update({ productCategory: value })}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={filters.includeFlights}
                  onChange={(event) => update({ includeFlights: event.target.checked })}
                />
              }
              label="Include flights"
              title="On by default, as in Tableau. Off removes every flight product, charter and Custom Offer flights included."
            />
            <FormControlLabel
              control={
                <Switch
                  checked={filters.removeAddonMigration}
                  onChange={(event) => update({ removeAddonMigration: event.target.checked })}
                />
              }
              label="Remove addon migration"
            />
            {BOARD_SELECTS.map(({ key, label }) => (
              <FilterSelect
                key={key}
                label={label}
                options={options[key] ?? []}
                value={filters.selections[key]}
                onChange={(value) => update({ selections: { ...filters.selections, [key]: value } })}
              />
            ))}
          </Stack>
        </Collapse>
        {failure && <Alert severity="error">{failure.message ?? 'The board could not load.'}</Alert>}
        <Grid container spacing={1.5}>
          {tiles.map((tile) => (
            <Grid key={tile.label} size={{ xs: 6, md: 2 }}>
              <KpiTile
                label={tile.label}
                value={tile.value}
                format={tile.format}
                note={tile.note}
                loading={!ready && !kpiCard}
              />
            </Grid>
          ))}
        </Grid>
        <Tabs
          data-lens-pdf-ignore=""
          value={filters.view}
          onChange={(_, value: BoardView) => update({ view: value })}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ '& .MuiTab-root': { textTransform: 'none' } }}
        >
          {BOARD_VIEWS.map((view) => (
            <Tab key={view.value} value={view.value} label={view.label} />
          ))}
        </Tabs>
        {views[filters.view].body}
        <Typography
          data-lens-pdf-ignore=""
          variant="caption"
          color="text.secondary"
          sx={{ overflowWrap: 'anywhere', pb: 2 }}
        >
          Source: {SOURCE}, rebuilt from the workbook’s legacy payment-type table. Monthly views cover 13 months, yearly
          views 3 calendar years and refunds 5 quarters, all to yesterday; the Daily View shows the last 3 days, and the
          period selector drives the tiles, Net Sales and Net Sales by Supplier. Growth compares with the previous
          window of the same length. Flights are included unless switched off. On sale rows, cash, credit, promo and
          airline, points and outstanding actual sales add up to gross sales, with vouchers inside outstanding as in
          Tableau.
        </Typography>
      </Stack>
    </Container>
  );
}

export default function DailyBoardPage({ printTarget }: NativePrintProps) {
  return (
    <NativeReportShell
      pageId="daily-board"
      title="Daily Sales Transaction Breakdown"
      printTarget={printTarget}
      cards="marked"
      includeHeading={false}
    >
      <DailyBoardContent />
    </NativeReportShell>
  );
}
